import React, { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Button, Drawer, Icon, useDrawer } from '@/components/shared';
import { DrawerBody } from '@/components/shared/Drawer/DrawerBody';
import { DrawerFooter } from '@/components/shared/Drawer/DrawerFooter';
import { DrawerHeader } from '@/components/shared/Drawer/DrawerHeader';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { Area, PageType } from '@/generated/data.schemas';
import { useGetLayout } from '@/generated/layout';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import { DrawerMegaMenu } from '../DrawerMegaMenu';

interface MobileMenuProps {
	triggerButtonClassName?: string;
}

export const MobileMenu: React.FunctionComponent<MobileMenuProps> = ({ triggerButtonClassName }) => {
	const navigate = useNavigate();
	const { segmentationId } = useContext(SegmentationContext);
	const { data: layout } = useGetLayout(segmentationId);
	const { data: settings } = useGetSettings(segmentationId);
	const user = settings?.data.user;
	const staticLinks = layout?.data.staticLinks;
	const { data: translations } = useGetTranslations(segmentationId);
	const loginUrl = staticLinks?.find((link) => link.pageType === PageType.UserSignIn)?.url;
	const ariaControlId = 'mobile-menu';

	const drawerRef = React.useRef<HTMLDivElement>(null);
	const { isOpen, isRendered, handleOpen, handleClose } = useDrawer(drawerRef);

	const location = useLocation();

	const goToLogin = (): void => {
		// Calling login this way makes sure mobile menu closes,
		// even if the user is already on the login page
		navigate(
			{ pathname: loginUrl },
			{ state: { area: Area.StaticPages, pageType: PageType.UserSignIn, returnTo: location.pathname } },
		);

		handleClose();
	};

	return (
		<>
			{/* Trigger button */}
			<Button
				className={triggerButtonClassName}
				variant={'ghost'}
				hasOnlyIcon
				onClick={handleOpen}
				aria-label={translations?.data.shared.menuLabel || 'Menu'}
				aria-expanded={isOpen}
				aria-controls={ariaControlId}
			>
				<Icon name={'burger'} size={'md'} />
			</Button>

			{/* Mobile menu */}
			{isRendered && (
				<Drawer drawerRef={drawerRef} isOpen={isOpen} id={ariaControlId}>
					<DrawerHeader handleClose={handleClose} />
					<DrawerBody>
						<DrawerMegaMenu isMenuOpen={isOpen} handleCloseMenu={handleClose} />
					</DrawerBody>
					{user && (
						<DrawerFooter>
							<Button size={'sm'} variant={'secondary'} isFullWidth onClick={goToLogin}>
								<span>{translations?.data.auth.signin.login}</span>
							</Button>
						</DrawerFooter>
					)}
				</Drawer>
			)}
		</>
	);
};
